<template>
  <div>
    <h4 class="table-page-header">
      Offices
      <el-tooltip content="Add New Office">
        <el-button type="text" @click="createOffice">
          <i class="fa-solid fa-circle-plus" style="font-size: 22px"></i>
        </el-button>
      </el-tooltip>
    </h4>
    <div class="filter-section">
      <div class="filter-row">
        <div class="filter-group search-group">
          <el-input
            v-model="searchQuery"
            placeholder="Search"
            clearable
            @clear="clearSearch"
            @input="onSearch"
            class="search-input"
          >
            <i
              slot="prepend"
              class="fa fa-search"
              style="padding-left: 4px; padding-right: 4px"
            ></i>
          </el-input>
        </div>
      </div>
    </div>
    <AIVTable
      :columns="officeColumns"
      :data="offices"
      :pagination="pagination"
      :item-name="'offices'"
      @pageChange="fetchOffices"
      @pageSizeChange="handlePageSizeChange"
      @sort-change="handleSortChange"
    >
      <template #cell-actions="{ row }">
        <button
          class="btn btn-sm btn-primary mr-2"
          @click="openUsor95Modal(row)"
          title="Generate USOR Form"
        >
          <i class="fa-solid fa-file-pdf"></i>
        </button>
        <button
          class="btn btn-sm btn-warning mr-2"
          @click="editOffice(row)"
          title="Edit"
        >
          <i class="fa-solid fa-pen-to-square"></i>
        </button>
        <button
          class="btn btn-sm btn-danger"
          @click="$confirmAction(() => deleteOffice(row))"
          title="Delete"
        >
          <i class="fa-solid fa-trash"></i>
        </button>
      </template>
    </AIVTable>
    <AIVDialog
      v-model="$modal.state.create_or_edit_office"
      :title="officeBeingEdited ? 'Edit Office' : 'Create a New Office'"
      width="90%"
      height="85vh"
      class="office-edit-modal"
    >
      <div class="row col-md-12 d-flex justify-content-between">
        <el-form
          ref="createForm"
          :model="office"
          label-position="top"
          label-width="120px"
        >
          <AIVRow
            :record.sync="office"
            :fields="[
              { label: 'Office Name', key: 'name' },
              { label: 'Phone', key: 'phone' },
            ]"
          />
          <AIVRow
            :record.sync="office"
            :fields="[
              { label: 'TTY Phone', key: 'phone_tty' },
              { label: 'Fax', key: 'fax' },
            ]"
          />
          <AIVRow
            :record.sync="office"
            :fields="[
              { label: 'Billing Email', key: 'billing_email' },
              {
                label: 'Payer',
                key: 'id_payers',
                type: 'select',
                options: payerOptions,
                placeholder: 'Select Payer',
              },
              {
                label: 'Admin',
                key: 'id_admin',
                type: 'select',
                options: adminOptions,
                placeholder: 'Select Admin',
              },
            ]"
          />
          <h5 class="mt-3">Address Information</h5>
          <div class="row mb-3 w-100 mx-0">
            <div class="col-md-6 px-2">
              <label class="d-block">Address Line 1</label>
              <AIVAddressAutocomplete
                :value="office.address ? office.address.address1 : ''"
                @input="val => { if (office.address) $set(office.address, 'address1', val); }"
                @place-selected="applyOfficePlace"
              />
            </div>
            <div class="col-md-6 px-2">
              <label class="d-block">Address Line 2</label>
              <el-input
                :value="office.address ? office.address.address2 : ''"
                @input="val => { if (office.address) $set(office.address, 'address2', val); }"
                placeholder="Address Line 2"
                class="w-100"
              />
            </div>
          </div>
          <AIVRow
            :record.sync="office.address"
            :fields="[
              { label: 'City', key: 'city' },
              { label: 'State', key: 'state' },
              { label: 'Zip', key: 'zip' },
            ]"
          />
        </el-form>
      </div>
      <template slot="footer">
        <div class="text-center">
          <el-button type="primary" round @click="saveOffice">{{
            officeBeingEdited ? "Update" : "Create"
          }}</el-button>
        </div>
      </template>
    </AIVDialog>
    <AIVDialog v-model="$modal.state.generate_form" width="500px">
      <template slot="title">
        <div class="modal-title-wrapper">
          <div class="modal-title-main">Generate Forms</div>
          <div class="modal-title-subtitle">
            {{ selectedOffice ? selectedOffice.name : "" }}
          </div>
        </div>
      </template>
      <div class="generate-form-content">
        <div class="form-field">
          <label class="field-label">Service Type</label>
          <AIVSelect
            v-model="usor95Data.serviceCodeId"
            :options="formSupportedServiceCodes"
            placeholder="Select Service Type"
          />
        </div>
        <div class="form-field">
          <label class="field-label">Month</label>
          <AIVSelect
            v-model="usor95Data.month"
            :options="monthOptionsForSelect"
            placeholder="Select Month"
          />
        </div>
        <div class="form-field">
          <label class="field-label">Year</label>
          <el-input-number
            v-model="usor95Data.year"
            :min="2020"
            :max="2050"
            style="width: 100%"
          />
        </div>
        <div class="form-field">
          <label class="field-label">Admin Notification Email</label>
          <el-input
            v-model="usor95Data.notificationEmail"
            placeholder="Admin email address"
            type="email"
            style="width: 100%"
          />
        </div>
        <div class="form-field">
          <label class="field-label">Additional Notification Emails</label>
          <el-input
            v-model="usor95Data.additionalEmails"
            placeholder="email1@example.com, email2@example.com"
            type="text"
            style="width: 100%"
          />
        </div>
      </div>
      <template slot="footer">
        <div class="text-center">
          <el-button type="primary" round @click="generateUsor95">
            Generate PDF
          </el-button>
        </div>
      </template>
    </AIVDialog>
  </div>
</template>

<script>
import { officesService } from "@/serviceHandlers/officesService";
import { payersService } from "@/serviceHandlers/payersService";
import { serviceCodesService } from "@/serviceHandlers/serviceCodesService";
import { adminUsersService } from "@/serviceHandlers/adminUsersService";
import AIVSelect from "@/components/AIVSelect.vue";
import AIVAddressAutocomplete from "@/components/AIVAddressAutocomplete.vue";
import { getFormKeyForType } from "@/constants/billingForms";

export default {
  name: "Offices",
  components: {
    AIVSelect,
    AIVAddressAutocomplete,
  },
  data() {
    return {
      officeBeingEdited: null,
      officeColumns: [
        { key: "name", label: "Office Name" },
        { key: "phone", label: "Phone" },
        { key: "billing_email", label: "Billing Email" },
        { key: "full_address", label: "Address", sortable: false, class: "hide-on-medium" },
        { key: "actions", label: "Actions", sortable: false },
      ],
      newOffice: {
        name: "",
        phone: "",
        phone_tty: "",
        fax: "",
        billing_email: "",
        id_payers: null,
        id_admin: null,
        address: {
          address1: "",
          address2: "",
          city: "",
          state: "",
          zip: "",
        },
      },
      searchQuery: "",
      sortProp: "",
      sortOrder: "",
      offices: [],
      pagination: {
        total: 0,
        perPage: 20,
        currentPage: 1,
        totalPages: 0,
      },
      payerOptions: [],
      adminOptions: [],
      serviceCodeOptions: [],
      selectedOffice: null,
      usor95Data: {
        serviceCodeId: null,
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear(),
        notificationEmail: "",
        additionalEmails: "",
      },
      monthOptions: [
        { value: 1, label: "January" },
        { value: 2, label: "February" },
        { value: 3, label: "March" },
        { value: 4, label: "April" },
        { value: 5, label: "May" },
        { value: 6, label: "June" },
        { value: 7, label: "July" },
        { value: 8, label: "August" },
        { value: 9, label: "September" },
        { value: 10, label: "October" },
        { value: 11, label: "November" },
        { value: 12, label: "December" },
      ],
    };
  },
  computed: {
    office: {
      get() {
        return this.officeBeingEdited || this.newOffice;
      },
      set(val) {
        if (this.officeBeingEdited) {
          this.officeBeingEdited = val;
        } else {
          this.newOffice = val;
        }
      },
    },
    formSupportedServiceCodes() {
      // Show any service code whose type maps to a USOR billing form (95, 148,
      // 96), resolved via the billing form registry. New codes appear here
      // automatically once their Type is set.
      return this.serviceCodeOptions
        .filter((sc) => getFormKeyForType(sc.type))
        .map((sc) => ({
          id: sc.id,
          name: sc.code, // Display the code instead of the full name
        }));
    },
    monthOptionsForSelect() {
      // Convert monthOptions to the format expected by AIVSelect
      return this.monthOptions.map((month) => ({
        id: month.value,
        name: month.label,
      }));
    },
  },
  async mounted() {
    await Promise.all([
      this.fetchOffices(),
      this.getPayers(),
      this.getAdmins(),
      this.getServiceCodes(),
    ]);
  },
  methods: {
    async fetchOffices(pageNumber = 1) {
      this.$spinner.show();
      try {
        const params = new URLSearchParams();
        params.append("page", pageNumber);
        params.append("perPage", this.pagination.perPage);

        if (this.searchQuery) {
          params.append("searchTerm", this.searchQuery);
        }

        if (this.sortProp) {
          params.append("sortField", this.sortProp);
          params.append("sortOrder", this.sortOrder);
        }

        const response = await officesService.listPaginated(params);
        this.offices = response.rows.map((office) => {
          let fullAddress = "";
          if (office.address) {
            const parts = [];
            if (office.address.address1) parts.push(office.address.address1);
            if (office.address.city) parts.push(office.address.city);
            if (office.address.state) parts.push(office.address.state);
            if (office.address.zip) parts.push(office.address.zip);
            fullAddress = parts.join(", ");
          }

          return {
            ...office,
            payer_name: office.payer ? office.payer.name : "No Payer Assigned",
            full_address: fullAddress || "No Address",
          };
        });
        this.pagination = response.pagination;
      } catch (error) {
        this.$notifyError("Could not load offices: " + error);
      } finally {
        this.$spinner.hide();
      }
    },
    onSearch() {
      this.pagination.currentPage = 1;
      this.fetchOffices(1);
    },
    clearSearch() {
      this.searchQuery = "";
      this.pagination.currentPage = 1;
      this.fetchOffices(1);
    },
    handleSortChange({ prop, order }) {
      this.sortProp = prop;
      this.sortOrder = order;
      this.fetchOffices(1);
    },
    handlePageSizeChange(newSize) {
      this.pagination.perPage = newSize;
      this.pagination.currentPage = 1;
      this.fetchOffices(1);
    },
    getOffices() {
      return officesService
        .list()
        .then((offices) => {
          this.offices = offices.map((office) => {
            let fullAddress = "";
            if (office.address) {
              const parts = [];
              if (office.address.address1) parts.push(office.address.address1);
              if (office.address.city) parts.push(office.address.city);
              if (office.address.state) parts.push(office.address.state);
              if (office.address.zip) parts.push(office.address.zip);
              fullAddress = parts.join(", ");
            }

            return {
              ...office,
              payer_name: office.payer
                ? office.payer.name
                : "No Payer Assigned",
              full_address: fullAddress || "No Address",
            };
          });
        })
        .catch((error) => {
          this.$notifyError("Could not load offices: " + error);
        });
    },
    getPayers() {
      return payersService
        .list()
        .then((payers) => {
          this.payerOptions = payers.map((payer) => ({
            id: payer.id,
            name: payer.name,
          }));
        })
        .catch((error) => {
          this.$notifyError("Could not load payers: " + error);
        });
    },
    getAdmins() {
      return adminUsersService
        .list()
        .then((admins) => {
          this.adminOptions = admins.map((admin) => ({
            id: admin.id,
            name: `${admin.first_name} ${admin.last_name}`.trim(),
            email: admin.email,
          }));
        })
        .catch((error) => {
          this.$notifyError("Could not load admins: " + error);
        });
    },
    getServiceCodes() {
      return serviceCodesService
        .list()
        .then((serviceCodes) => {
          this.serviceCodeOptions = serviceCodes.map((code) => ({
            id: code.id,
            name: code.name,
            code: code.code,
            type: code.type,
          }));
        })
        .catch((error) => {
          this.$notifyError("Could not load service codes: " + error);
        });
    },
    deleteOffice(office) {
      return officesService
        .delete(office.id)
        .then(() => {
          this.$notifySuccess("Successfully deleted office!");
          this.fetchOffices(this.pagination.currentPage);
        })
        .catch((error) => {
          this.$notifyError("Error deleting office: " + error);
        });
    },
    applyOfficePlace(place) {
      if (!this.office.address) this.$set(this.office, "address", {});
      this.$set(this.office.address, "address1", place.address1);
      this.$set(this.office.address, "city", place.city);
      this.$set(this.office.address, "state", place.state);
      this.$set(this.office.address, "zip", place.zip);
      this.$set(this.office.address, "geocode", place.geocode);
    },
    async saveOffice() {
      const officeObj = this.office;

      if (!officeObj.name) {
        this.$notifyError("No office name!");
        return;
      }
      if (!officeObj.phone) {
        this.$notifyError("No office phone!");
        return;
      }

      this.$spinner.show();
      let apiSaveCall;

      if (this.officeBeingEdited) {
        const updatePayload = {
          name: officeObj.name,
          phone: officeObj.phone,
          phone_tty: officeObj.phone_tty,
          fax: officeObj.fax,
          billing_email: officeObj.billing_email,
          id_payers: officeObj.id_payers,
          id_admin: officeObj.id_admin,
        };

        if (officeObj.address && officeObj.address.address1) {
          updatePayload.address = officeObj.address;
        }

        apiSaveCall = officesService
          .update(officeObj.id, updatePayload)
          .then(() => {
            this.$notifySuccess("Successfully updated office.");
          });
      } else {
        const createPayload = {
          name: officeObj.name,
          phone: officeObj.phone,
          phone_tty: officeObj.phone_tty,
          fax: officeObj.fax,
          billing_email: officeObj.billing_email,
          id_payers: officeObj.id_payers,
          id_admin: officeObj.id_admin,
        };

        if (officeObj.address && officeObj.address.address1) {
          createPayload.address = officeObj.address;
        }

        apiSaveCall = officesService.create(createPayload).then(() => {
          this.$notifySuccess("Successfully created office.");
        });
      }
      apiSaveCall
        .catch((error) => {
          this.$notifyError("Error: " + error);
        })
        .then(() => {
          return this.fetchOffices();
        })
        .finally(() => {
          this.$spinner.hide();
          this.$modal.close("create_or_edit_office");
          this.officeBeingEdited = null;
        });
    },
    createOffice() {
      this.officeBeingEdited = null;
      this.newOffice = {
        name: "",
        phone: "",
        phone_tty: "",
        fax: "",
        billing_email: "",
        id_payers: null,
        id_admin: null,
        address: {
          address1: "",
          address2: "",
          city: "",
          state: "",
          zip: "",
        },
      };
      this.$modal.open("create_or_edit_office");
    },
    async editOffice(office) {
      this.officeBeingEdited = {
        ...office,
        id_admin:
          office.admins && office.admins.length > 0
            ? office.admins[0].id
            : null,
        address: office.address || {
          address1: "",
          address2: "",
          city: "",
          state: "",
          zip: "",
        },
      };
      this.$modal.open("create_or_edit_office");
    },
    async openUsor95Modal(office) {
      this.selectedOffice = office;

      // Find the admin email for this office
      let adminEmail = "";
      if (office.admins && office.admins.length > 0) {
        const adminId = office.admins[0].id;
        const admin = this.adminOptions.find((a) => a.id === adminId);
        if (admin && admin.email) {
          adminEmail = admin.email;
        }
      }

      this.usor95Data = {
        serviceCodeId: null,
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear(),
        notificationEmail: adminEmail,
        additionalEmails: "",
      };
      this.$modal.open("generate_form");
    },
    async generateUsor95() {
      if (!this.usor95Data.serviceCodeId) {
        this.$notifyError("Please select a service type");
        return;
      }
      if (!this.usor95Data.month) {
        this.$notifyError("Please select a month");
        return;
      }
      if (!this.usor95Data.year) {
        this.$notifyError("Please enter a year");
        return;
      }

      this.$spinner.show();
      try {
        const {
          serviceCodeId,
          month,
          year,
          notificationEmail,
          additionalEmails,
        } = this.usor95Data;
        const officeId = this.selectedOffice.id;

        // Combine admin email with additional emails
        let allEmails = [];
        if (notificationEmail) {
          allEmails.push(notificationEmail);
        }
        if (additionalEmails) {
          // Split by comma and trim whitespace
          const additional = additionalEmails
            .split(",")
            .map((email) => email.trim())
            .filter((email) => email.length > 0);
          allEmails = allEmails.concat(additional);
        }

        // Validate that at least one email is provided
        if (allEmails.length === 0) {
          this.$notifyError("At least one valid email address is required");
          this.$spinner.hide();
          return;
        }

        // Join all emails back into a comma-separated string
        const emailsToSend = allEmails.join(",");

        // Find the selected service code to determine which endpoint to call
        const selectedServiceCode = this.serviceCodeOptions.find(
          (sc) => sc.id === serviceCodeId,
        );

        // Determine which API to call based on the service code's billing form
        // (resolved from its Type via the billing form registry).
        const formKey = selectedServiceCode
          ? getFormKeyForType(selectedServiceCode.type)
          : null;
        let response;
        if (formKey === "USOR95") {
          // Job Coaching
          response = await officesService.generateUsor95Pdfs(
            officeId,
            serviceCodeId,
            month,
            year,
            emailsToSend,
          );
        } else if (formKey === "USOR148") {
          // Life Skills Coaching (Life Skills, Service Brokering, Supported Education)
          response = await officesService.generateUsor148Pdfs(
            officeId,
            serviceCodeId,
            month,
            year,
            emailsToSend,
          );
        } else if (formKey === "USOR96") {
          // Job Development
          response = await officesService.generateUsor96Pdfs(
            officeId,
            serviceCodeId,
            month,
            year,
            emailsToSend,
          );
        } else {
          this.$notifyError(
            "Selected service code is not supported for form generation",
          );
          return;
        }

        // Build detailed success message
        let message = `Successfully generated ${response.count || 0} PDF(s)`;
        if (response.pdfs && response.pdfs.length > 0) {
          const pdfList = response.pdfs
            .map((pdf) => `${pdf.clientName} (Auth: ${pdf.authNumber})`)
            .join(", ");
          message += `:\n${pdfList}`;
        }

        // Show errors if any
        if (response.errors && response.errors.length > 0) {
          const errorList = response.errors
            .map((err) => `${err.clientName} - ${err.error}`)
            .join(", ");
          message += `\n\nErrors: ${errorList}`;
        }

        this.$notifySuccess(message);
        this.$modal.close("generate_form");
      } catch (error) {
        // Handle "no authorizations" as informational rather than error
        const errorMessage = error.response?.data?.error || error.message;
        if (errorMessage.includes("No clients found with approved")) {
          this.$notifyError(errorMessage);
          this.$modal.close("generate_form");
        } else {
          this.$notifyError("Error generating PDFs: " + errorMessage);
        }
      } finally {
        this.$spinner.hide();
      }
    },
  },
};
</script>

<style scoped>
.modal-title-wrapper {
  text-align: left;
}

.modal-title-main {
  font-size: 18px;
  font-weight: 600;
  color: #fff;
  margin-bottom: 4px;
}

.modal-title-subtitle {
  font-size: 14px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.7);
}

.generate-form-content {
  padding: 10px 0;
}

.form-field {
  margin-bottom: 24px;
}

.field-label {
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  margin: 0 0 2px 0;
  display: block;
}

/* Compact filter section */
.filter-section {
  margin-bottom: 1.5rem;
}

.filter-row {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.5rem 0;
}

.filter-group {
  display: flex;
  align-items: center;
  min-width: 0;
  flex-shrink: 0;
}

.filter-group.search-group {
  flex: 1;
  max-width: 400px;
}

.search-input {
  width: 100%;
}

.compact-filter {
  min-width: 150px;
  width: 150px;
}

@media (max-width: 768px) {
  .filter-row {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
  }

  .filter-group.search-group {
    max-width: unset;
  }

  .compact-filter {
    width: 100%;
    min-width: unset;
  }
}
</style>
